// Razorpay payment-downtimes — admin-only proxy.
//
// Backend route: GET /v1/admin/razorpay/downtimes. payouts-backend forwards
// to api.razorpay.com/v1/payments/downtimes/ with the credential pair held
// in env vars, caches 60s, and returns the raw Razorpay response body
// unchanged. We type the slice we actually render here.
//
// `not_configured` (HTTP 503): the backend ships without the env vars
// populated. Caller renders an "operator needs to set RAZORPAY_KEY /
// RAZORPAY_SECRET" empty state.
import { apiClient } from "./client";

export type DowntimeStatus = "scheduled" | "started" | "resolved" | "cancelled";
export type DowntimeSeverity = "low" | "medium" | "high";

// Razorpay's instrument map varies per method:
//   netbanking → { bank: "DLXB" }
//   card       → { issuer: "BKID" } or { network: "VISA" }
//   upi        → { vpa_handle: "ibl" } or { psp: "google_pay" }
//   wallet     → { wallet: "phonepe" }
//   fpx        → { bank: "BNPA_C" }
// We keep it loose — operators want to see whatever Razorpay sent, even if
// we don't have a typed mapping for it yet.
export type DowntimeInstrument = Record<string, string>;

export interface RazorpayDowntime {
  id: string;
  entity: "payment.downtime";
  method: string;
  begin: number; // Unix epoch seconds
  end: number | null;
  status: DowntimeStatus;
  scheduled: boolean;
  severity: DowntimeSeverity;
  instrument: DowntimeInstrument;
  created_at: number;
  updated_at: number;
}

interface RazorpayDowntimesResponse {
  entity: "collection";
  count: number;
  items: RazorpayDowntime[];
}

export async function listRazorpayDowntimes(): Promise<RazorpayDowntime[]> {
  const resp = await apiClient.get<RazorpayDowntimesResponse>(
    "/v1/admin/razorpay/downtimes",
  );
  return resp.data.items ?? [];
}
